(function () {
  const ACTIVE_CLASS = 'theme-page-transition--active';
  const SELECTOR = '[data-page-transition]';
  const SESSION_KEY = 'themePageTransitionSeen';
  const TRANSITION_CHANCE = 1;
  const MOBILE_BREAKPOINT = '(max-width: 959px)';
  const MOBILE_DURATION_MULTIPLIER = 1;

  const parseTime = (value) => {
    if (!value) return 0;
    const trimmed = value.trim();
    if (trimmed.endsWith('ms')) return Number.parseFloat(trimmed);
    if (trimmed.endsWith('s')) return Number.parseFloat(trimmed) * 1000;
    return Number.parseFloat(trimmed);
  };

  const getDuration = (el) => {
    const dataValue = Number.parseFloat(el.getAttribute('data-page-transition-duration'));
    if (!Number.isNaN(dataValue) && dataValue >= 0) return dataValue;

    const styles = window.getComputedStyle(el);
    const durations = styles.transitionDuration.split(',').map(parseTime);
    const delays = styles.transitionDelay.split(',').map(parseTime);
    const length = Math.max(durations.length, delays.length);
    let max = 0;

    for (let i = 0; i < length; i += 1) {
      const duration = durations[i] ?? durations[0] ?? 0;
      const delay = delays[i] ?? delays[0] ?? 0;
      const total = duration + delay;
      if (total > max) max = total;
    }

    return max;
  };

  const preloadImage = (url) => {
    if (!url) return Promise.resolve();

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });
  };

  const isSamePageHash = (url) => (
    url.origin === window.location.origin
    && url.pathname === window.location.pathname
    && url.search === window.location.search
    && url.hash
  );

  const normalizePatterns = (raw) => raw
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value);

  const matchesAlwaysPattern = (url, patterns) => patterns.some((pattern) => {
    if (isAbsoluteUrl(pattern)) {
      return url.href.startsWith(pattern);
    }

    if (pattern.startsWith('/')) {
      if (pattern.includes('?')) {
        return `${url.pathname}${url.search}`.startsWith(pattern);
      }
      return url.pathname.startsWith(pattern);
    }

    return url.href.includes(pattern);
  });

  const shouldIgnoreLink = (link, event) => {
    if (event.defaultPrevented) return true;
    if (event.button !== 0) return true;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return true;
    if (link.hasAttribute('download')) return true;
    if (link.getAttribute('role') === 'button') return true;
    if (link.hasAttribute('data-page-transition-ignore') || link.closest('[data-page-transition-ignore]')) return true;
    if (link.hasAttribute('data-no-transition')) return true;

    const target = link.getAttribute('target');
    if (target && target !== '_self') return true;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return true;

    const lowerHref = href.toLowerCase();
    if (lowerHref.startsWith('mailto:') || lowerHref.startsWith('tel:') || lowerHref.startsWith('javascript:')) {
      return true;
    }

    return false;
  };

  const hasSeenTransition = () => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch (error) {
      return false;
    }
  };

  const markTransitionSeen = () => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, 'true');
    } catch (error) {
      // Ignore storage failures and fall back to transition behavior for this page load.
    }
  };

  const init = () => {
    const transitionEl = document.querySelector(SELECTOR);
    if (!transitionEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const baseDuration = getDuration(transitionEl) || 400;
    const isMobile = window.matchMedia(MOBILE_BREAKPOINT).matches;
    const duration = Math.round(baseDuration * (isMobile ? MOBILE_DURATION_MULTIPLIER : 1));
    const alwaysPatterns = normalizePatterns(transitionEl.getAttribute('data-page-transition-always') || '');
    const transitionDesktopImage = transitionEl.getAttribute('data-page-transition-image-desktop') || '';
    const transitionMobileImage = transitionEl.getAttribute('data-page-transition-image-mobile') || '';

    // Keep transition media warm in cache on every page load.
    Promise.all([
      preloadImage(transitionDesktopImage),
      preloadImage(transitionMobileImage),
    ]);

    transitionEl.style.setProperty('--page-transition-duration', `${duration}ms`);

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || shouldIgnoreLink(link, event)) return;

      const url = new URL(link.href, window.location.href);
      if (isSamePageHash(url)) return;
      if (prefersReducedMotion) return;
      if (hasSeenTransition()) return;
      if (!matchesAlwaysPattern(url, alwaysPatterns) && Math.random() > TRANSITION_CHANCE) return;

      event.preventDefault();
      markTransitionSeen();

      transitionEl.classList.add(ACTIVE_CLASS);

      window.setTimeout(() => {
        window.location.href = url.href;
      }, duration);
    });

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      transitionEl.classList.remove(ACTIVE_CLASS);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
